import { ApiError } from '@/api/client'
import { useRecipeImage } from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/toastContext'
import { ImagePlus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif'
const MAX_BYTES = 5 * 1024 * 1024

/**
 * Photo picker for a saved recipe. The file is uploaded straight away, so the
 * recipe row and the file on disk never disagree about where the image lives.
 */
export function RecipeImageField({
  recipeId,
  imageUrl,
}: {
  recipeId: number
  imageUrl: string | null
}) {
  const toast = useToast()
  const image = useRecipeImage()
  const input = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)

  // Object URLs are ours to release; the server URL is not.
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview)
  }, [preview])

  const shown = preview ?? imageUrl
  const busy = image.upload.isPending || image.remove.isPending

  async function choose(file: File | undefined) {
    if (!file) return
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Choose a JPEG, PNG, WebP or GIF image.')
      return
    }
    if (file.size > MAX_BYTES) {
      toast.error('That image is too large. The limit is 5 MB.')
      return
    }
    setPreview(URL.createObjectURL(file))
    try {
      await image.upload.mutateAsync({ id: recipeId, file })
      toast.success('Photo uploaded.')
    } catch (error) {
      setPreview(null)
      toast.error(error instanceof ApiError ? error.message : 'Upload failed.')
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  async function clear() {
    try {
      await image.remove.mutateAsync(recipeId)
      setPreview(null)
      toast.success('Photo removed.')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not remove it.')
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-label font-medium text-ink-soft">Photo</span>
      <div className="flex items-start gap-3">
        {shown ? (
          <img
            src={shown}
            alt=""
            className="size-24 shrink-0 rounded-[var(--radius-field)] border border-line object-cover"
          />
        ) : (
          <div className="flex size-24 shrink-0 items-center justify-center rounded-[var(--radius-field)] border border-dashed border-line-strong text-ink-faint">
            <ImagePlus size={22} aria-hidden />
          </div>
        )}
        <div className="flex min-w-0 flex-col items-start gap-1.5">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(event) => void choose(event.target.files?.[0])}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={busy}
            onClick={() => input.current?.click()}
          >
            {shown ? 'Replace' : 'Upload'}
          </Button>
          {shown ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void clear()}
            >
              <Trash2 size={13} aria-hidden />
              Remove
            </Button>
          ) : null}
          <p className="text-micro text-pretty text-ink-faint">
            Optional. JPEG, PNG, WebP or GIF, up to 5 MB.
          </p>
        </div>
      </div>
    </div>
  )
}
